/**
 * The loop's centerline as a Three.js curve.
 *
 * The generator hands us piecewise CurveSegs at channel-floor height; we sample
 * them densely and refit a closed centripetal Catmull-Rom so the marble gets a
 * C1 path with cheap arc-length lookup.
 */
import * as THREE from "three";
import type { LoopPath } from "../shared/types.ts";
import { segLength, segPoint } from "../gen/geom.ts";

/** Target spacing between samples, in grid units. */
const SAMPLE_SPACING = 0.25;
const EPS = 1e-4;

export interface Centerline {
  curve: THREE.CatmullRomCurve3;
  /** Second lap variant taking every diamond's OTHER branch (if any). */
  altCurve?: THREE.CatmullRomCurve3;
  points: THREE.Vector3[];
  debugLine(): THREE.Line;
}

function buildVariant(loop: LoopPath, useAlt: boolean): THREE.Vector3[] {
  const points: THREE.Vector3[] = [];

  const push = (x: number, y: number, z: number) => {
    const last = points[points.length - 1];
    if (last) {
      const d2 = (last.x - x) ** 2 + (last.y - y) ** 2 + (last.z - z) ** 2;
      if (d2 < EPS * EPS) return;
    }
    points.push(new THREE.Vector3(x, y, z));
  };

  for (const seg of loop.segments) {
    const curve = useAlt && seg.alt ? seg.alt.curve : seg.curve;
    for (const part of curve) {
      const len = segLength(part);
      const steps = Math.max(1, Math.ceil(len / SAMPLE_SPACING));
      // i from 0 keeps joints exact; `push` drops the duplicate at each seam.
      for (let i = 0; i <= steps; i++) {
        const p = segPoint(part, i / steps);
        push(p[0], p[1], p[2]);
      }
    }
  }

  // Closed curve: drop the final point if it coincides with the first.
  if (points.length > 1) {
    const first = points[0];
    const last = points[points.length - 1];
    if (first.distanceToSquared(last) < EPS * EPS) points.pop();
  }
  if (points.length < 4) {
    // Degenerate loop — pad so CatmullRomCurve3 stays well-defined.
    while (points.length < 4) {
      points.push(points[points.length - 1].clone().addScalar(EPS));
    }
  }
  return points;
}

function fit(points: THREE.Vector3[]): THREE.CatmullRomCurve3 {
  const curve = new THREE.CatmullRomCurve3(points, true, "centripetal", 0.5);
  curve.arcLengthDivisions = points.length * 8;
  curve.updateArcLengths();
  return curve;
}

/**
 * Open-path variant (endless mode): same dense sampling + centripetal
 * Catmull-Rom refit, but the curve does not close — segment boundaries meet
 * at straight ports, so chained per-segment curves stay visually C1.
 */
export function buildOpenCurve(path: LoopPath): THREE.CatmullRomCurve3 {
  const points = buildVariant(path, false);
  const curve = new THREE.CatmullRomCurve3(points, false, "centripetal", 0.5);
  curve.arcLengthDivisions = points.length * 8;
  curve.updateArcLengths();
  return curve;
}

export function buildCenterline(loop: LoopPath): Centerline {
  const points = buildVariant(loop, false);
  const curve = fit(points);
  const hasAlt = loop.segments.some((s) => s.alt);
  const altCurve = hasAlt ? fit(buildVariant(loop, true)) : undefined;

  return {
    curve,
    altCurve,
    points,
    debugLine(): THREE.Line {
      const geom = new THREE.BufferGeometry().setFromPoints([
        ...points,
        points[0],
      ]);
      const mat = new THREE.LineBasicMaterial({
        color: 0xff00ff,
        depthTest: false,
        depthWrite: false,
        transparent: true,
      });
      const line = new THREE.Line(geom, mat);
      line.name = "centerline-debug";
      line.renderOrder = 999;
      return line;
    },
  };
}
